import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import { FaSearch, FaImage, FaCar, FaBox, FaClipboard, FaEye, FaTimes } from 'react-icons/fa';
import copyToClipboard from './clipboard';
import VistaProducto from '../pages/Productos/VistaProducto/VistaProducto';

/**
 * Helper to format vehicle applications string
 */
const formatAplicaciones = (aplicaciones) => {
    if (!aplicaciones) return '';

    // Split by semicolon and clean up
    const list = String(aplicaciones).split(';').map(s => s.trim()).filter(Boolean);

    const grouped = {};
    list.forEach(app => {
        const parts = app.split(' ');
        const marca = parts[0];
        const model = parts.slice(1).join(' '); // Everything after first word

        if (!marca || !model) return;

        if (!grouped[marca]) grouped[marca] = [];
        if (!grouped[marca].includes(model)) grouped[marca].push(model);
    });

    return Object.keys(grouped).map(marca => {
        return `${marca} ${grouped[marca].join(', ')}`;
    }).join(' / ');
};

export default function AutocompleteSearch({
    isMobile = false,
    onCloseMobile,
    onProductSelect,
    variant = 'navbar',
    placeholder = 'Buscar repuestos, SKU, vehículo...',
}) {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [previewProduct, setPreviewProduct] = useState(null); // State for modal

    const navigate = useNavigate();
    const location = useLocation();
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const token = Cookies.get('token');

    // Debounce logic
    useEffect(() => {
        const fetchSuggestions = async () => {
            if (query.length < 2) {
                setSuggestions([]);
                return;
            }

            setIsLoading(true);
            setShowSuggestions(true);
            try {
                const response = await axios.get(`/api/productos/autocomplete?q=${encodeURIComponent(query)}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                setSuggestions(response.data);
                setShowSuggestions(true);
            } catch (error) {
                console.error("Error fetching suggestions:", error);
            } finally {
                setIsLoading(false);
            }
        };

        const timeoutId = setTimeout(fetchSuggestions, 400);
        return () => clearTimeout(timeoutId);
    }, [query, token]);

    // Handle clicks outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    // Keyboard navigation
    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex >= 0 && suggestions[selectedIndex]) {
                handleSelectProduct(suggestions[selectedIndex]);
            } else {
                handleSearch();
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            if (isMobile && onCloseMobile) onCloseMobile();
        }
    };

    const handleSearch = () => {
        if (query.trim()) {
            console.log('Search triggered for:', query);
            setShowSuggestions(false);
            if (isMobile && onCloseMobile) onCloseMobile();
        }
    };

    const handleSelectProduct = (product) => {
        if (onProductSelect) {
            onProductSelect(product);
        } else {
            // Navigate to product view page
            navigate(`/productos/vista/${product.id_producto}`);
        }
        setShowSuggestions(false);
        setQuery('');
        if (isMobile && onCloseMobile) onCloseMobile();
    };

    const handleCopyBarcode = (e, barcode) => {
        e.stopPropagation();
        copyToClipboard(barcode);
    };

    const handlePreview = (e, product) => {
        e.stopPropagation();
        setPreviewProduct(product);
        setShowSuggestions(false); // Close suggestions when opening modal
    };

    const closePreview = () => {
        setPreviewProduct(null);
    };

    // Auto-focus input on mobile open
    useEffect(() => {
        if (isMobile && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isMobile]);

    // Close modal on location change (navigation)
    useEffect(() => {
        setPreviewProduct(null);
    }, [location]);

    const getInputStyles = () => {
        if (isMobile) {
            return 'bg-gray-100 text-gray-800 placeholder-gray-500 focus:ring-2 focus:ring-yellow-500 border-transparent';
        }
        if (variant === 'light' || variant === 'embedded') {
            return 'bg-white text-gray-800 placeholder-gray-500 border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 shadow-sm';
        }
        // Default 'navbar'
        return 'bg-white/10 text-white placeholder-gray-300 hover:bg-white/20 focus:bg-white/20 focus:ring-2 focus:ring-yellow-500 border-transparent';
    };

    const getIconColor = () => {
        if (isMobile || variant === 'light' || variant === 'embedded') {
            return 'text-gray-400 hover:text-amber-600';
        }
        return 'text-gray-300 hover:text-yellow-400';
    };

    return (
        <>
            <div ref={wrapperRef} className={`relative w-full ${isMobile ? '' : 'max-w-2xl'}`}>
                <div className="relative">
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder={placeholder}
                        className={`w-full py-2 pl-4 pr-10 ${variant === 'embedded' ? 'rounded-lg' : 'rounded-full'} border-2 transition-all font-medium ${getInputStyles()}`}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onFocus={() => {
                            if (suggestions.length > 0) setShowSuggestions(true);
                        }}
                        onKeyDown={handleKeyDown}
                    />
                    <button
                        onClick={handleSearch}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors ${getIconColor()}`}
                    >
                        <FaSearch />
                    </button>
                </div>

                {showSuggestions && (suggestions.length > 0 || isLoading) && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-gray-900/95 backdrop-blur-md rounded-b-xl shadow-xl z-50 overflow-hidden border border-gray-700 max-h-[70vh] overflow-y-auto">
                        {isLoading && suggestions.length === 0 ? (
                            <div className="flex items-center justify-center p-4 gap-2">
                                <svg className="animate-spin h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                <span className="text-sm text-gray-400 font-medium">Buscando...</span>
                            </div>
                        ) : (
                            <ul>
                                {suggestions.map((product, index) => (
                                    <li
                                        key={product.id_producto}
                                        onClick={() => handleSelectProduct(product)}
                                        className={`flex items-center p-3 cursor-pointer border-b border-gray-700 last:border-0 transition-colors group
                                        ${index === selectedIndex ? 'bg-white/10' : 'hover:bg-white/10'}
                                    `}
                                    >
                                        {/* Image Thumbnail */}
                                        <div className="w-12 h-12 flex-shrink-0 bg-white rounded overflow-hidden mr-3 flex items-center justify-center border border-gray-600">
                                            {product.image ? (
                                                <img
                                                    src={product.image}
                                                    alt={product.nombre}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => { e.target.onerror = null; e.target.src = '' }} // Fallback if 404
                                                />
                                            ) : (
                                                <FaImage className="text-gray-400 text-lg" />
                                            )}
                                        </div>

                                        {/* Product Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-start">
                                                <p className="text-sm font-bold text-gray-100 truncate pr-2 group-hover:text-yellow-400 transition-colors">
                                                    {product.nombre}
                                                </p>
                                                <span className="text-sm font-bold text-green-400 whitespace-nowrap">
                                                    {product.precio_venta}
                                                </span>
                                            </div>

                                            <div className="flex flex-col gap-0.5 mt-0.5">
                                                {/* Brand, SKU & Measures & Barcode */}
                                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                                                    {product.marca && (
                                                        <span className="text-blue-300 font-bold uppercase tracking-wide">
                                                            {product.marca}
                                                        </span>
                                                    )}
                                                    <span className="text-gray-400 font-mono">
                                                        SKU: {product.sku}
                                                    </span>
                                                    {product.codigo_barras && (
                                                        <span className="text-gray-500 font-mono flex items-center gap-1">
                                                            CB: {product.codigo_barras}
                                                        </span>
                                                    )}
                                                    {product.medidas && (
                                                        <span className="text-gray-300 bg-gray-700/50 px-1 rounded border border-gray-600">
                                                            {product.medidas}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Applications */}
                                                {product.aplicaciones && (
                                                    <div className="flex items-start gap-1 text-[10px] text-gray-400 leading-tight mt-1">
                                                        <FaCar className="mt-0.5 flex-shrink-0" />
                                                        <span className="italic line-clamp-1">
                                                            {formatAplicaciones(product.aplicaciones)}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex flex-col gap-2 ml-2 pl-2 border-l border-gray-700">
                                            {/* Copy Barcode Button */}
                                            {product.codigo_barras && (
                                                <button
                                                    onClick={(e) => handleCopyBarcode(e, product.codigo_barras)}
                                                    className="p-1.5 text-gray-400 hover:text-white hover:bg-blue-600 rounded transition-colors"
                                                    title="Copiar Código de Barras"
                                                >
                                                    <FaClipboard />
                                                </button>
                                            )}
                                            {/* Preview Button */}
                                            <button
                                                onClick={(e) => handlePreview(e, product)}
                                                className="p-1.5 text-gray-400 hover:text-white hover:bg-yellow-500 rounded transition-colors"
                                                title="Vista Rápida"
                                            >
                                                <FaEye />
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            {/* Product Preview Modal */}
            {previewProduct && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={closePreview}>
                    <div
                        className="relative w-full max-w-6xl h-[90vh] bg-transparent rounded-xl overflow-hidden shadow-2xl animate-fade-in-up"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header stick at top */}
                        <div className="absolute top-0 right-0 left-0 p-4 flex justify-end z-50 pointer-events-none">
                            <button
                                onClick={closePreview}
                                className="pointer-events-auto bg-gray-900/80 text-white p-2 rounded-full hover:bg-red-600 hover:scale-110 transition-all shadow-lg backdrop-blur-sm"
                            >
                                <FaTimes className="text-xl" />
                            </button>
                        </div>

                        <div className="h-full overflow-y-auto custom-scrollbar">
                            {/* Padding top to avoid close button overlap if header is transparent, 
                               but VistaProducto has its own header. 
                               We'll just render it. The Close button is absolute on top. */}
                            <VistaProducto id_producto={previewProduct.id_producto} token={token} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
